import React, { useState, useCallback, useEffect } from 'react';
import { 
  styled, 
  alpha,
  AppBar, 
  Box,
  Button,
  Toolbar, 
  IconButton, 
  Typography, 
  InputBase, 
  Badge, 
  MenuItem, 
  Menu,
  Avatar,
  Tooltip,
  ClickAwayListener,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Clear as ClearIcon,
  Mail as MailIcon,
  MailOutline as MailOutlineIcon,
  Settings as SettingsIcon,
  Article as ArticleIcon,
  MenuBook as MenuBookIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useUser } from "shared/helpers/axios";
import { useNotifications } from "components/comm/notifications/contexts/NotificationContext";
import { useUserStatus, USER_STATUSES, getStatusMeta } from "shared/contexts/UserStatusContext";
import { useUserActions } from "shared/hooks/user.actions";
import { useUserConsortia } from "shared/hooks/useUserConsortia";
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';
import logo from "media/otable_main.png";
import NotificationMenu from 'components/common/layout/dropdowns/NotificationMenu';
import MessagesMenu from './dropdowns/MessagesMenu';
import AdminMenu from './dropdowns/AdminMenu';
import { useTheme } from '@mui/material/styles';

const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Simple debounce implementation
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  zIndex: 1500
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '25px',
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
  '&:focus-within': {
    backgroundColor: alpha(theme.palette.common.white, 0.3),
    border: '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)',
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.7)',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'white',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.2, 1, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingRight: theme.spacing(4),
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: '0.95rem',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.7)',
      opacity: 1,
    },
    [theme.breakpoints.up('md')]: {
      width: '300px',
      '&:focus': {
        width: '400px',
      },
    },
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'rgba(255, 255, 255, 0.7)',
  padding: theme.spacing(0.5),
  '&:hover': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const SearchResults = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1300,
  marginTop: theme.spacing(1),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  maxHeight: '400px',
  overflowY: 'auto',
  overflowX: 'hidden', // Prevent horizontal scrolling
  width: '97%',
  minWidth: '350px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
}));

const SearchResultItem = styled(ListItem)(({ theme }) => ({
  borderRadius: '12px',
  margin: theme.spacing(0.5, 1),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    transform: 'translateX(4px)',
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.7rem',
  height: '20px',
  borderRadius: '10px',
}));

const MyAppBar = ({ onToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [consortiaAnchorEl, setConsortiaAnchorEl] = useState(null);
  const [proposalsAnchorEl, setProposalsAnchorEl] = useState(null);
  const [bookmarksAnchorEl, setBookmarksAnchorEl] = useState(null);
  const [messageAnchorEl, setMessageAnchorEl] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const theme = useTheme();

  // Use notification context
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
  } = useNotifications();
  
  const { consortia: userConsortia, mutate: mutateConsortia } = useUserConsortia();
  const [userProposals, setUserProposals] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [currentPageBookmarked, setCurrentPageBookmarked] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const userActions = useUserActions();
  const { status: userStatus, setStatus: setUserStatus } = useUserStatus();
  const statusMeta = getStatusMeta(userStatus);

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);
  const isConsortiaMenuOpen = Boolean(consortiaAnchorEl);
  const isProposalsMenuOpen = Boolean(proposalsAnchorEl);
  const isMessageMenuOpen = Boolean(messageAnchorEl);

  const menuId = 'primary-search-account-menu';
  const notificationMenuId = 'notifications-menu';
  const consortiaMenuId = 'consortia-menu';
  const proposalsMenuId = 'proposals-menu';
  const messageMenuId = 'messages-menu';

  // Load initial data on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // fetchBookmarks();
    fetchUnreadMessagesCount();
    fetchConversations();
    
    // Listen for messages page opened event to refresh count
    const handleMessagesPageOpened = () => {
      fetchUnreadMessagesCount();
      fetchConversations();
    };

    // Listen for consortium created/joined events to refresh the menu + chip
    const handleConsortiumCreated = () => mutateConsortia();

    window.addEventListener('messagesPageOpened', handleMessagesPageOpened);
    window.addEventListener('consortiumCreated', handleConsortiumCreated);

    return () => {
      window.removeEventListener('messagesPageOpened', handleMessagesPageOpened);
      window.removeEventListener('consortiumCreated', handleConsortiumCreated);
    };
  }, []);

  const fetchUnreadMessagesCount = async () => {
    try {
      const response = await axiosService.get('/interact/messages/unread-count/');
      setUnreadMessagesCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread messages count:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axiosService.get('/interact/messages/conversations/');
      setConversations(response.data || []);

      // Pass conversations directly to MessagesMenu (not extracted messages)
      // MessagesMenu will handle the conversation structure
      // console.log("***** CONVERSATIONS *** ", response.data);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      setConversations([]);
      setMessages([]);
    }
  };

  // Update bookmark status when location changes
  useEffect(() => {
    const isBookmarked = bookmarks.some(bookmark => bookmark.url === location.pathname);
    setCurrentPageBookmarked(isBookmarked);
  }, [location.pathname, bookmarks]);

  // Fetch user's proposals on mount
  useEffect(() => {
    if (user?.id) {
      fetchUserProposals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchUserProposals = async () => {
    try {
      const response = await axiosService.get('/proposal/proposals/');
      setUserProposals(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch user proposals:', error);
      setUserProposals([]);
    }
  };

  // const fetchBookmarks = async () => {
  //   // Mock bookmarks data
  //   const mockBookmarks = [
  //     {
  //       id: 1,
  //       title: 'Horizon Europe - Digital Health Call',
  //       type: 'funding_call',
  //       url: '/funding/call/123',
  //       deadline: '2024-03-15',
  //       saved: '3 days ago'
  //     },
  //     {
  //       id: 2,
  //       title: 'ERC Advanced Grant Guidelines',
  //       type: 'resource',
  //       url: '/funding/erc/guidelines',
  //       saved: '1 week ago'
  //     },
  //     {
  //       id: 3,
  //       title: 'Dr. Sarah Johnson - AI Researcher',
  //       type: 'profile',
  //       url: '/profile/456',
  //       saved: '2 days ago'
  //     },
  //     {
  //       id: 4,
  //       title: 'Machine Learning Consortium',
  //       type: 'consortium',
  //       url: '/consortium/789',
  //       saved: '1 day ago'
  //     }
  //   ];
  //   setBookmarks(mockBookmarks);
    
  //   const isBookmarked = mockBookmarks.some(bookmark => bookmark.url === location.pathname);
  //   setCurrentPageBookmarked(isBookmarked);
  // };

  const handleNotificationClick = async (notification) => {
    const payload = notification.payload || {};
    let navigationPath = null;

    const ntype = notification.ntype || notification.type;
    
    switch (ntype) {
      case 'comment':
        if (payload.target_type === 'reflection' && payload.reflection_author_id && 
            payload.reflection_content_type && payload.reflection_object_id) {
          navigationPath = `/profile/${payload.reflection_author_id}/reflections/${payload.reflection_content_type}/${payload.reflection_object_id}`;
        }
        break;
      case 'consortium_invitation':
        if (payload.consortium_id) {
          navigationPath = `/consortia/${payload.consortium_id}`;
        }
        break;
      case 'meeting_request':
      case 'meeting_poll':
        if (payload.meeting_id) {
          navigationPath = `/meetings/${payload.meeting_id}`;
        }
        break;
      case 'invitation':
        if (payload.invitation_id) {
          navigationPath = `/invitations/${payload.invitation_id}`;
        }
        break;
      default:
        if (notification.link) {
          navigationPath = notification.link;
        }
    }

    handleNotificationMenuClose();

    if (navigationPath) {
      navigate(navigationPath);
    }

    if (!notification.read && !notification.is_read) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  // Search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      
      try {
        const [callsRes, consortiaRes, usersRes, orgsRes, labsRes] = await Promise.allSettled([
          axiosService.get(`/funding/calls/?search=${encodeURIComponent(query)}&page_size=3`),
          axiosService.get(`/consortium/consortiums/?search=${encodeURIComponent(query)}&page_size=3`),
          axiosService.get(`/users/?search=${encodeURIComponent(query)}&page_size=3`),
          axiosService.get(`/consortium/organizations/?search=${encodeURIComponent(query)}&page_size=3`),
          axiosService.get(`/consortium/labs/?search=${encodeURIComponent(query)}&page_size=3`),
        ]);

        const results = [];

        // Process funding calls
        if (callsRes.status === 'fulfilled' && callsRes.value?.data) {
          const calls = Array.isArray(callsRes.value.data) 
            ? callsRes.value.data 
            : callsRes.value.data.results || [];
          
          calls.forEach(call => {
            results.push({
              id: call.id,
              type: 'call',
              title: call.title,
              description: call.description || call.call_type || 'Funding Call',
              agency: call.agency?.name || call.program?.name || '',
              deadline: call.stage1_deadline || call.deadline,
              url: `/funding/call/${call.id}`
            });
          });
        }

        // Process consortia
        if (consortiaRes.status === 'fulfilled' && consortiaRes.value?.data) {
          const consortia = Array.isArray(consortiaRes.value.data)
            ? consortiaRes.value.data
            : consortiaRes.value.data.results || [];
          
          consortia.forEach(consortium => {
            results.push({
              id: consortium.id,
              type: 'consortium',
              title: consortium.name,
              description: `${consortium.memberships?.length || 0} members • ${consortium.status || 'Active'}`,
              members: consortium.memberships?.length || 0,
              url: `/consortium/${consortium.id}`
            });
          });
        }

        // Process users (exclude current user)
        if (usersRes.status === 'fulfilled' && usersRes.value?.data) {
          const users = Array.isArray(usersRes.value.data)
            ? usersRes.value.data
            : usersRes.value.data.results || [];
          
          users.forEach(u => {
            if (String(u.id) === String(user?.id)) return;
            results.push({
              id: u.id,
              type: 'user',
              avatar: u.avatar || u.avatar_url || undefined,    // the person's photo in the list
              title: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
              description: u.position || u.bio || 'User',
              affiliation: u.affiliation || u.department || '',
              url: `/profile/${u.id}`
            });
          });
        }

        // Process organizations
        if (orgsRes.status === 'fulfilled' && orgsRes.value?.data) {
          const orgs = Array.isArray(orgsRes.value.data)
            ? orgsRes.value.data
            : orgsRes.value.data.results || [];

          orgs.forEach(org => {
            results.push({
              id: org.id,
              type: 'organization',
              title: org.name,
              description: org.org_type ? org.org_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Organization',
              url: `/organization/${org.id}/`
            });
          });
        }

        // Process labs
        if (labsRes.status === 'fulfilled' && labsRes.value?.data) {
          const labs = Array.isArray(labsRes.value.data) ? labsRes.value.data : labsRes.value.data.results || [];
          labs.slice(0, 3).forEach(lab => {
            results.push({
              id: lab.id,
              type: 'lab',
              title: lab.name,
              description: [lab.organization_detail?.name, lab.pi_detail?.name && `PI ${lab.pi_detail.name}`].filter(Boolean).join(' · ') || 'Lab',
              avatar: lab.cover_url || undefined,
              url: `/lab/${lab.id}/`
            });
          });
        }

        setSearchResults(results);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    performSearch(searchQuery);
  }, [searchQuery, performSearch]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleResultClick = (url) => {
    navigate(url);
    setShowResults(false);
    setSearchQuery('');
  };

  // Status submenu
  const [statusSubmenuAnchor, setStatusSubmenuAnchor] = useState(null);
  const handleStatusMenuOpen = (e) => setStatusSubmenuAnchor(e.currentTarget);
  const handleStatusMenuClose = () => setStatusSubmenuAnchor(null);

  // Menu handlers
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setStatusSubmenuAnchor(null);
  };
  const handleLogout = () => {
    userActions.logout();
    handleMenuClose();
  };

  const getResultColor = (type) => {
    switch (type) {
      case 'call': return 'primary';
      case 'consortium': return 'secondary';
      case 'user': return 'default';
      case 'agency': return 'info';
      case 'organization': return 'warning';
      case 'lab': return 'success';
      default: return 'default';
    }
  };

  // Notification menu handlers
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    if (unreadCount > 0) {
      markAllAsRead();
    }
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  // Consortia menu handlers
  const handleConsortiaMenuOpen = (event) => setConsortiaAnchorEl(event.currentTarget);
  const handleConsortiaMenuClose = () => setConsortiaAnchorEl(null);

  // Proposals menu handlers
  const handleProposalsMenuOpen = (event) => {
    setProposalsAnchorEl(event.currentTarget);
    fetchUserProposals();
  };
  const handleProposalsMenuClose = () => setProposalsAnchorEl(null);

  // Bookmarks menu handlers
  // Messages menu handlers
  const handleMessageMenuOpen = (event) => {
    setMessageAnchorEl(event.currentTarget);
    // Refresh messages when opening the menu
    fetchConversations();
  };

  const handleMessageMenuClose = () => {
    setMessageAnchorEl(null);
  };

  const handleMessageClick = async (message) => {
    handleMessageMenuClose();
    
    if (message.conversation_id) {
      navigate(`/messages/?user=${message.conversation_id}`);
    } else if (message.partner?.id) {
      navigate(`/messages/?user=${message.partner.id}`);
    } else {
      navigate('/messages/');
    }
    
    // Mark as read if not already read
    if (!message.is_read && !message.read) {
      try {
        await axiosService.patch(`/interact/messages/${message.id}/mark-read/`, {
          is_read: true,
        });
        fetchUnreadMessagesCount();
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleMarkAllMessagesAsRead = async () => {
    try {
      await axiosService.post('/interact/messages/mark-all-read/');
      setUnreadMessagesCount(0);
      fetchConversations();
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  const handleNewMessage = () => {
    navigate('/messages/new/');
    handleMessageMenuClose();
  };

  

  // Render menus
  const renderProposalsMenu = (
    <Menu
      anchorEl={proposalsAnchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={proposalsMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isProposalsMenuOpen}
      onClose={handleProposalsMenuClose}
      PaperProps={{
        style: {
          borderRadius: '12px',
          marginTop: '8px',
          minWidth: '320px',
          maxHeight: '400px'
        }
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box flex={1}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            My Works
          </Typography>
          <Typography variant="caption" color="text.secondary">Proposals and publications you are writing</Typography>
        </Box>
        <Button size="small" onClick={() => { handleProposalsMenuClose(); navigate('/works/'); }} sx={{ textTransform: 'none', fontSize: 13 }}>
          See all
        </Button>
      </Box>
      <List dense>
        {userProposals.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No works yet"
              secondary="Start a proposal or a publication from a consortium to see it here"
              sx={{ textAlign: 'center' }}
            />
          </ListItem>
        ) : (
          [...userProposals]
            .sort((a, b) => new Date(b.last_edited || b.updated || 0) - new Date(a.last_edited || a.updated || 0))
            .map((proposal) => {
              const isPublication = proposal.document_type === 'publication';
              const title = (proposal.title || '').trim() || 'Untitled';
              const clip = (t, n) => (t.length > n ? `${t.slice(0, n).trimEnd()}…` : t);
              // The acronym alone can repeat (several manuscripts per consortium): always show the title too
              const heading = proposal.acronym || clip(title, 28);
              return (
                <Tooltip key={proposal.id} title={title} placement="left" enterDelay={600}>
                  <ListItem
                    button
                    onClick={() => {
                      navigate(`/consortium/${proposal.consortium}/${isPublication ? 'publications' : 'proposals'}/${proposal.id}/`);
                      handleProposalsMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      {isPublication
                        ? <MenuBookIcon sx={{ color: '#0f766e', fontSize: 'large' }} />
                        : <ArticleIcon sx={{ color: '#7c3aed', fontSize: 'large' }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" lineHeight={1.15} noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
                            {heading}
                          </Typography>
                          <Chip
                            size="small"
                            label={isPublication ? 'Publication' : 'Proposal'}
                            sx={{
                              height: 18, fontSize: '0.7rem', fontWeight: 600, flexShrink: 0,
                              bgcolor: isPublication ? 'rgba(15,118,110,0.1)' : 'rgba(124,58,237,0.1)',
                              color: isPublication ? '#0f766e' : '#7c3aed',
                            }}
                          />
                          <Box sx={{ flex: 1 }} />
                          {(proposal.last_edited || proposal.updated) && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0, fontSize: '0.85rem' }}>
                              {timeAgo(proposal.last_edited || proposal.updated)}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {proposal.acronym && (
                            <Typography component="span" variant="caption" display="block" noWrap color="text.primary" 
                                        sx={{ fontWeight: 500, fontSize: '0.85rem', lineHeight: 1.2, mb: 0.2, mt: 0.2 }}>
                              {clip(title, 45)}
                            </Typography>
                          )}
                          <Typography component="span" variant="caption" display="block" noWrap color="text.secondary">
                            {proposal.consortium_name} · {proposal.status || 'Draft'}
                            {isPublication && proposal.target_journal ? ` · ${proposal.target_journal}` : ''}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </Tooltip>
              );
            })
        )}
      </List>
    </Menu>
  );

  const renderConsortiaMenu = (
    <Menu
      anchorEl={consortiaAnchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={consortiaMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isConsortiaMenuOpen}
      onClose={handleConsortiaMenuClose}
      PaperProps={{
        style: {
          borderRadius: '12px',
          marginTop: '8px',
          minWidth: '320px',
          maxHeight: '400px'
        }
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          My Consortia
        </Typography>
      </Box>
      <List dense>
        {userConsortia.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No consortia yet"
              secondary="Join or create consortia to see them here"
              sx={{ textAlign: 'center' }}
            />
          </ListItem>
        ) : (
          [...userConsortia]
            .sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0))
            .map((consortium) => (
              <ListItem
                key={consortium.id}
                button
                onClick={() => {
                  navigate(`/consortium/${consortium.id}/`);
                  handleConsortiaMenuClose();
                }}
              >
                <ListItemIcon>
                  <GroupIcon sx={{ color: '#7c3aed', fontSize: 'large' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body1" lineHeight={1.1} sx={{ fontWeight: 500 }}>
                        {consortium.name}
                      </Typography>
                      {consortium.updated && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0, fontSize: '0.85rem' }}>
                          {timeAgo(consortium.updated)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {consortium.member_count || 0} members
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#7c3aed', fontSize: '0.7rem' }}>
                        {consortium.status_display || consortium.status}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
        )}
        <Divider />
        <ListItem button onClick={() => { navigate('/consortium/'); handleConsortiaMenuClose(); }}>
          <ListItemText 
            primary="View all consortia" 
            sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 600 }}
          />
        </ListItem>
      </List>
    </Menu>
  );

  // const renderBookmarksMenu = (
  //   <Menu
  //     anchorEl={bookmarksAnchorEl}
  //     anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  //     id={bookmarksMenuId}
  //     keepMounted
  //     transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  //     open={isBookmarksMenuOpen}
  //     onClose={handleBookmarksMenuClose}
  //     PaperProps={{
  //       style: {
  //         borderRadius: '12px',
  //         marginTop: '8px',
  //         minWidth: '320px',
  //         maxHeight: '400px'
  //       }
  //     }}
  //   >
  //     <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
  //       <Typography variant="h6" sx={{ fontWeight: 600 }}>
  //         Bookmarks
  //       </Typography>
  //     </Box>
  //     <List dense>
  //       {bookmarks.length === 0 ? (
  //         <ListItem>
  //           <ListItemText
  //             primary="No bookmarks yet"
  //             secondary="Bookmark calls, profiles, and resources for quick access"
  //             sx={{ textAlign: 'center' }}
  //           />
  //         </ListItem>
  //       ) : (
  //         bookmarks.map((bookmark) => {
  //           const getBookmarkIcon = (type) => {
  //             switch (type) {
  //               case 'funding_call': return <CampaignIcon color="primary" />;
  //               case 'consortium': return <GroupIcon color="secondary" />;
  //               case 'profile': return <PersonIcon color="success" />;
  //               case 'resource': return <StarIcon color="warning" />;
  //               default: return <BookmarkIcon />;
  //             }
  //           };

  //           return (
  //             <ListItem 
  //               key={bookmark.id}
  //               button 
  //               onClick={() => { navigate(bookmark.url); handleBookmarksMenuClose(); }}
  //             >
  //               <ListItemIcon>
  //                 {getBookmarkIcon(bookmark.type)}
  //               </ListItemIcon>
  //               <ListItemText
  //                 primary={
  //                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
  //                     {bookmark.title}
  //                   </Typography>
  //                 }
  //                 secondary={
  //                   <Box>
  //                     <Typography variant="caption" color="text.secondary">
  //                       {bookmark.type.replace('_', ' ')} • Saved {bookmark.saved}
  //                     </Typography>
  //                     {bookmark.deadline && (
  //                       <Typography variant="caption" display="block" sx={{ color: 'warning.main', fontSize: '0.7rem' }}>
  //                         Deadline: {bookmark.deadline}
  //                       </Typography>
  //                     )}
  //                   </Box>
  //                 }
  //               />
  //             </ListItem>
  //           );
  //         })
  //       )}
  //       <Divider />
  //       <ListItem button onClick={() => { navigate('/bookmarks/'); handleBookmarksMenuClose(); }}>
  //         <ListItemText 
  //           primary="Manage bookmarks" 
  //           sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 600 }}
  //         />
  //       </ListItem>
  //     </List>
  //   </Menu>
  // );

  const renderProfileMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        style: {
          minWidth: '160px',
          borderRadius: '12px',
          marginTop: '45px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
        }
      }}
    >
      <MenuItem onClick={() => { navigate(`/profile/${user?.id}/`); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
        <PersonIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="body" fontWeight={500}>My Profile</Typography>
      </MenuItem>
      <MenuItem onClick={() => { navigate(`/settings/`); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
        <SettingsIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="body" fontWeight={500}>Settings</Typography>
      </MenuItem>
      <MenuItem onClick={() => { navigate('/blog/'); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
        <ArticleIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="body" fontWeight={500}>About Otable</Typography>
      </MenuItem>
      <Divider />
      {/* Status — opens submenu */}
      <MenuItem onClick={handleStatusMenuOpen} sx={{ gap: 1.5, py: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusMeta.color, flexShrink: 0 }} />
          <Typography variant="body" fontWeight={500}>Status</Typography>
          <Typography variant="caption" sx={{ color: statusMeta.color, fontWeight: 600 }}>{statusMeta.label}</Typography>
        </Box>
        <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1, color: 'error.main' }}>
        <LogoutIcon fontSize="small" />
        <Typography variant="body" fontWeight={500}>Logout</Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <StyledAppBar position="fixed">
        <Toolbar>


          {/* Logo + Hamburger */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0.5, md: 2 }, flexShrink: 0 }}>
            {/* Logo — full on md+, "O" crop on xs with hamburger overlaid */}
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Box
                sx={{ cursor: 'pointer', overflow: 'hidden', height: 45, width: { xs: 45, md: 'auto' } }}
                onClick={() => navigate('/')}
              >
                <img src={logo} 
                      alt="Otable" style={{ height: '45px', width: 'auto', maxWidth: 'none', display: 'block' }} />
              </Box>

              {/* Mobile: hamburger centered over the "O" */}
              {onToggle && (
                <IconButton
                  onClick={onToggle}
                  size="small"
                  aria-label="toggle sidebar"
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    position: 'absolute',
                    top: '50%',
                    left: '49%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    // bgcolor: 'rgba(0,0,0,0.32)',
                    p: '3px',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.52)' },
                  }}
                >
                  <MenuIcon sx={{ fontSize: 17 }} />
                </IconButton>
              )}
            </Box>

            {/* Desktop: hamburger right of logo */}
            {onToggle && (
              <IconButton
                onClick={onToggle}
                size="medium"
                aria-label="toggle sidebar"
                sx={{ color: 'white', ml: 2, display: { xs: 'none', md: 'inline-flex' }, 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
              >
                <MenuIcon sx={{ fontSize: 17 }} />
              </IconButton>
            )}
          </Box>

          {/* Search Bar Container */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            maxWidth: '600px',
            mx: 2,
          }}>
            <ClickAwayListener onClickAway={() => setShowResults(false)}>
              <Box sx={{ 
                position: 'relative', 
                width: '100%',
                maxWidth: '500px'
              }}>
                <Search>
                  <SearchIconWrapper>
                    {isSearching ? (
                      <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    ) : (
                      <SearchIcon />
                    )}
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder="Search calls, consortiums, researchers..."
                    inputProps={{ 'aria-label': 'search' ,
                      style: {
                        fontSize: '1.2rem', // Adjust font size for the placeholder text
                      },
                     }}
                   
                    value={searchQuery}
                    onChange={(e) => { e.stopPropagation(); handleSearchChange(e); }}
                    onClick={(e) => { e.stopPropagation();}}
                    onFocus={(e) => {
                      e.stopPropagation();
                      if (searchResults.length > 0) setShowResults(true);
                    }}
                
                  />
                  {searchQuery && (
                    <ClearButton onClick={clearSearch} size="small">
                      <ClearIcon fontSize="small" />
                    </ClearButton>
                  )}
                </Search>

                {/* Search Results */}
                {showResults && searchResults.length > 0 && (
                  <SearchResults>
                    <List dense>
                      {searchResults.map((result) => (
                        <SearchResultItem
                          key={`${result.type}-${result.id}`}
                          button
                          onClick={() => handleResultClick(result.url)}
                        >
                          {/* 
                            {getResultIcon(result.type)}
                          */}
<ListItemIcon>
              <Avatar 
                src={result?.avatar} 
                alt={result?.name || result?.title || 'Search result'}
                sx={{ width: 38, height: 38, mr: 1.5, fontSize: '1.5rem',
                  backgroundColor: theme.palette.primary.avatar,
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                  mb:1,
                  mt:0.5
                  // border: `1px solid ${theme.palette.sidebar.active}`,
                  // mb: 0.5
                 }}
              >

                    { (result?.name || result?.title || '?')[0] }
              </Avatar>
 </ListItemIcon>

                          <ListItemText
                            primary={
                              <Box sx={{ pl:0, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {result.title}
                                </Typography>
                                <CategoryChip
                                  label={result.type}
                                  size="small"
                                  color={getResultColor(result.type)}
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={result.description}
                          />
                        </SearchResultItem>
                      ))}
                    </List>
                  </SearchResults>
                )}
              </Box>
            </ClickAwayListener>
          </Box>

          {/* Right Side Actions */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
            mr: 2,
            '& .MuiSvgIcon-root': {
              fontSize: 20,
            },
          }}>


            {/* Admin pages: superusers only */}
            {user?.is_superuser && <AdminMenu />}


            {/* Proposals */}
            <Tooltip title="My Works">
              <IconButton
                size="large"
                color="inherit"
                onClick={handleProposalsMenuOpen}
                aria-label="My Works"
                aria-controls={isProposalsMenuOpen ? proposalsMenuId : undefined}
                aria-haspopup="true"
                sx={{ display: 'block' }}
              >
                <ArticleIcon />
              </IconButton>
            </Tooltip>

            {/* Consortia */}
            <Tooltip title="My Consortia">
              <IconButton
                size="large"
                color="inherit"
                onClick={handleConsortiaMenuOpen}
                aria-label="My Consortia"
                aria-controls={isConsortiaMenuOpen ? consortiaMenuId : undefined}
                aria-haspopup="true"
                sx={{ display: 'block' }}
              >
                {/* <Badge
                  badgeContent={userConsortia.length || 0}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16, padding: '0 4px' } }}
                > */}
                  <GroupIcon/>
                {/* </Badge> */}
             </IconButton>
          </Tooltip>

            {/* Bookmarks */}
            {/* <Tooltip title={`${currentPageBookmarked ? 'Remove bookmark' : 'Bookmark this page'}`}>
              <IconButton 
                size="large" 
                color="inherit"
                onClick={toggleCurrentPageBookmark}
              >
                {currentPageBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
             */}
            {/* Bookmarks Menu */}
            {/* <Tooltip title="View all bookmarks">
              <IconButton 
                size="large" 
                color="inherit"
                onClick={handleBookmarksMenuOpen}
                aria-controls={isBookmarksMenuOpen ? bookmarksMenuId : undefined}
                aria-haspopup="true"
              >
                <Badge badgeContent={bookmarks.length} color="info">
                  <BookmarkIcon />
                </Badge>
              </IconButton>
            </Tooltip> */}


            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton 
                size="large" 
                color="inherit"
                onClick={handleNotificationMenuOpen}
                aria-label="Notifications"
                aria-controls={isNotificationMenuOpen ? notificationMenuId : undefined}
                aria-haspopup="true"
                sx={{ 
                  display: 'block'
                }}
              >
                <Badge badgeContent={unreadCount} color="error"   
                 anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiBadge-badge': {
                    transform: 'translate(15px, -5px)', // Adjust these values
                  },
                }}>
                  <NotificationsIcon />
                </Badge>

              </IconButton>
            </Tooltip>

            {/* Messages */}
            <Tooltip title="Messages">
              <IconButton 
                size="large" 
                color="inherit"
                onClick={handleMessageMenuOpen}
                aria-label="Messages"
                aria-controls={isMessageMenuOpen ? messageMenuId : undefined}
                aria-haspopup="true"
                sx={{
                  display: 'block'
                }} 
              >
                <Badge badgeContent={unreadMessagesCount} color="error"    
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiBadge-badge': {
                        transform: 'translate(15px, -5px)', // Adjust these values
                      }
                      }}
                    >
                  {unreadMessagesCount > 0 ? <MailIcon /> : <MailOutlineIcon />}
                </Badge>
              </IconButton>
            </Tooltip>


            {/* Profile Menu */}
            <Tooltip title={`Account · ${statusMeta.label}`}>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent=""
                  sx={{
                    '& .MuiBadge-badge': {
                      minWidth: 8, width: 8, height: 8,
                      borderRadius: '50%',
                      bgcolor: statusMeta.color,
                      border: '1px solid',
                      borderColor: 'rgba(255,255,255,0.9)',
                      p: 0,
                    },
                  }}
                >
                  {user?.avatar ? (
                    <Avatar
                      src={user.avatar}
                      alt={user?.username || 'User avatar'}
                      sx={{ width: 32, height: 32,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        fontSize: 14, fontWeight: 600,
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{ width: 30, height: 30,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        fontSize: 14, fontWeight: 600,
                      }}
                      alt={user?.username || 'User'}
                    >
                      {(user?.username?.[0] || 'U').toUpperCase()}
                    </Avatar>
                  )}
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </StyledAppBar>
      
      {/* Menus */}
      {renderProfileMenu}
      {/* Status submenu */}
      <Menu
        anchorEl={statusSubmenuAnchor}
        open={Boolean(statusSubmenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { minWidth: 190, borderRadius: '12px' } }}
      >
        <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.65rem', color: 'text.disabled' }}>
            Set Status
          </Typography>
        </Box>
        {USER_STATUSES.map(s => (
          <MenuItem
            key={s.key}
            onClick={() => { setUserStatus(s.key); handleStatusMenuClose(); }}
            sx={{ gap: 1.5, py: 0.9, bgcolor: userStatus === s.key ? `${s.color}0f` : 'transparent' }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={userStatus === s.key ? 700 : 400} sx={{ color: userStatus === s.key ? s.color : 'text.primary', lineHeight: 1.3 }}>
                {s.label}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.95rem' }}>{s.desc}</Typography>
            </Box>
            {userStatus === s.key && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />}
          </MenuItem>
        ))}
      </Menu>
      {renderProposalsMenu}
      {renderConsortiaMenu}
      {/* {renderBookmarksMenu} */}

      <NotificationMenu 
        notifications={notifications}
        notificationAnchorEl={notificationAnchorEl}
        isNotificationMenuOpen={isNotificationMenuOpen}
        unreadCount={unreadCount}
        onClose={handleNotificationMenuClose}
        onNotificationClick={handleNotificationClick}
        onMarkAllAsRead={markAllAsRead}
        notificationMenuId={notificationMenuId}
      />

      <MessagesMenu
        messages={messages}
        messageAnchorEl={messageAnchorEl}
        isMessageMenuOpen={isMessageMenuOpen}
        unreadCount={unreadMessagesCount}
        onClose={handleMessageMenuClose}
        onMessageClick={handleMessageClick}
        onMarkAllAsRead={handleMarkAllMessagesAsRead}
        onNewMessage={handleNewMessage}
        messageMenuId={messageMenuId}
      />
    </Box>
  );
};

export default MyAppBar;